import { MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { Collapse, Space, theme, Typography } from 'antd';
import { type ReactNode } from 'react';
import { type EditableSiteMenuOrder, type SiteMenuOrdersBySite } from './customer-order-form.utils';
import SiteMenuOrderForm from './site-menu-order/site-menu-order.form';
import DetailedSite from '../../../../components/detailted-site';
import { type CustomerSetting, type Site } from '../../../../entities';
import { type LocalDate } from '../../../../utils/local-date';
import { totalMeals } from '../../../../utils/order-utils';
import utils from '../../../../utils/utils';

// Shared by the sites without site menu orders, so their form isn't rendered again when another
// site changes
const NO_SITE_MENU_ORDERS: EditableSiteMenuOrder[] = [];

const SitePanelExtra = ({
  isEditing,
  mealsCount,
}: {
  isEditing: boolean;
  mealsCount: number;
}): ReactNode => {
  const { token } = theme.useToken();
  return (
    <Space size="large">
      {isEditing && (
        <Typography.Text strong style={{ color: token.colorWarning }} disabled>
          Update In Progress...
        </Typography.Text>
      )}
      <Typography.Text disabled>
        {utils.numberFormat(mealsCount)} meal{mealsCount !== 1 ? 's' : ''}
      </Typography.Text>
    </Space>
  );
};

interface SitesCollapseProps {
  sites: Site[];
  siteMenuOrdersBySite: SiteMenuOrdersBySite;
  editingSiteIds: ReadonlySet<string>;
  orderDate: LocalDate | undefined;
  customerSetting: CustomerSetting | undefined;
  defaultActiveSiteId: string | null;
  onSiteMenuOrdersChange: (siteId: string, siteMenuOrders: EditableSiteMenuOrder[]) => void;
  onEditingChange: (siteId: string, isEditing: boolean) => void;
}

/** One panel for each site of the order, holding its site menu orders */
const SitesCollapse = ({
  sites,
  siteMenuOrdersBySite,
  editingSiteIds,
  orderDate,
  customerSetting,
  defaultActiveSiteId,
  onSiteMenuOrdersChange,
  onEditingChange,
}: SitesCollapseProps): ReactNode => {
  // Each site's form starts again when the date changes: the meals it offered don't apply anymore
  const orderDateKey = orderDate?.toISOString() ?? '';

  return (
    <Collapse
      expandIcon={({ isActive }) => (isActive ? <MinusSquareOutlined /> : <PlusSquareOutlined />)}
      defaultActiveKey={defaultActiveSiteId ?? []}
      bordered={false}
      accordion
      items={sites.map((site) => {
        const siteMenuOrders = siteMenuOrdersBySite[site.id!] ?? NO_SITE_MENU_ORDERS;
        console.log('siteMenuOrders', siteMenuOrders);
        return {
          key: site.id!,
          label: <DetailedSite strong site={site} />,
          collapsible: 'header',
          children: (
            <SiteMenuOrderForm
              key={`${site.id}-${orderDateKey}`}
              site={site}
              siteMenuOrders={siteMenuOrders}
              customerSetting={customerSetting}
              orderDate={orderDate}
              onSiteMenuOrdersChange={onSiteMenuOrdersChange}
              onEditingChange={onEditingChange}
            />
          ),
          extra: (
            <SitePanelExtra
              isEditing={editingSiteIds.has(site.id!)}
              mealsCount={totalMeals(siteMenuOrders)}
            />
          ),
        };
      })}
    />
  );
};

export default SitesCollapse;
